/**
 * Single shared email validator for client and server. Framework-free ESM, no imports.
 *
 * Canonical home for finding XCUT-4 (email validation was duplicated three ways):
 *   - server/lib/validators.js          (validateEmail)
 *   - server/routes/emailCampaigns.js   (isValidEmail)
 *   - client/src/components/family/FamilyMemberManagement.jsx (isValidEmail)
 *
 * Imported by the server via a relative path and by the client via the `@shared` Vite alias.
 *
 * Rules (intentionally pragmatic and ReDoS-safe):
 *   - trims + lowercases before checking
 *   - one `@`, a dotted domain, no whitespace (single non-overlapping char classes)
 *   - rejects leading/trailing dots, `..`, `@.` and `.@` (catches addresses a bare
 *     `[^\s@]+@[^\s@]+\.[^\s@]+` regex would otherwise let through)
 *   - enforces RFC 5321 length bounds
 */

export const MAX_EMAIL_LENGTH = 254 // RFC 5321 upper bound for a full address
export const MAX_LOCAL_LENGTH = 64 // RFC 5321 upper bound for the local part

// Pragmatic and ReDoS-safe: single, non-overlapping character classes, no nested quantifiers.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * @param {unknown} email
 * @returns {string} trimmed, lowercased, or '' for non-strings
 */
export function normalizeEmail(email) {
  return typeof email === 'string' ? email.trim().toLowerCase() : ''
}

// IP-literal domains: dotted-quad (user@127.0.0.1) or bracketed RFC 5321 literal
// (user@[127.0.0.1], user@[IPv6:...]). Plain character classes, ReDoS-safe.
const DOTTED_QUAD_RE = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/

/**
 * @param {unknown} email
 * @param {{ allowIpDomain?: boolean }} [options]
 * @returns {boolean}
 */
export function isValidEmail(email, options) {
  return validateEmail(email, options).valid
}

/**
 * @param {unknown} email
 * @param {{ allowIpDomain?: boolean }} [options] allowIpDomain defaults to true (the shared
 *   validator's historical behavior); pass false where IP-literal recipient domains must be
 *   rejected (e.g. campaign lists, restoring the old `validator.isEmail` strictness there).
 * @returns {{ valid: boolean, normalized: string, error: string | null }}
 */
export function validateEmail(email, { allowIpDomain = true } = {}) {
  if (typeof email !== 'string' || email.length === 0) {
    return result(false, '', 'Email is required')
  }

  const normalized = normalizeEmail(email)
  if (normalized.length === 0) return result(false, '', 'Email cannot be empty')
  if (normalized.length > MAX_EMAIL_LENGTH) return result(false, normalized, 'Email is too long')

  // Edge-dot patterns the core regex would otherwise accept (e.g. ".a@b.com", "a.@b.com",
  // "a@.b.com", "a..b@c.com"). Kept as plain string checks to stay ReDoS-safe.
  if (
    normalized.startsWith('.') ||
    normalized.endsWith('.') ||
    normalized.includes('..') ||
    normalized.includes('@.') ||
    normalized.includes('.@')
  ) {
    return result(false, normalized, 'Invalid email format')
  }

  if (!EMAIL_RE.test(normalized)) return result(false, normalized, 'Invalid email format')

  const localPart = normalized.slice(0, normalized.indexOf('@'))
  if (localPart.length > MAX_LOCAL_LENGTH) {
    return result(false, normalized, 'Email local part is too long')
  }

  if (!allowIpDomain) {
    const domain = normalized.slice(normalized.indexOf('@') + 1)
    if (domain.startsWith('[') || DOTTED_QUAD_RE.test(domain)) {
      return result(false, normalized, 'Email domain cannot be an IP address')
    }
  }

  return result(true, normalized, null)
}

/**
 * @param {boolean} valid
 * @param {string} normalized
 * @param {string | null} error
 */
function result(valid, normalized, error) {
  return { valid, normalized, error }
}
